import axios, {AxiosResponse} from 'axios';
import {
  TaxReceiptRequest,
  FiscalDataListItem,
  CategoryWithTaxonomy,
  FiscalDataUpdatePayload,
} from '../models';

const TAX_RECEIPT_API_URL = process.env.REACT_APP_TAX_RECEIPT_API_URL;
const CATEGORIES_CATALOG_API_URL =
  process.env.REACT_APP_CATEGORIES_CATALOG_API_URL;

// Flattens tax receipt requests into individual items missing fiscal data
const flattenItemsWithMissingFiscalData = (
  requests: TaxReceiptRequest[]
): FiscalDataListItem[] => {
  const items: FiscalDataListItem[] = [];

  requests.forEach((request) => {
    request.items_meta.forEach((itemMeta) => {
      // Only include items missing ncm OR cest_id
      if (!itemMeta.ncm || !itemMeta.cest_id) {
        items.push({
          id: request.id,
          item_id: itemMeta.id,
          internal_reference_id: request.internal_reference_id,
          product_mpi: itemMeta.product_mpi,
          description: itemMeta.description,
          quantity: itemMeta.quantity,
          unit_value: itemMeta.unit_value,
          created_at: request.created_at,
          ncm: itemMeta.ncm,
          cest_id: itemMeta.cest_id,
          invalid_ncm: itemMeta.invalid_ncm,
          invalid_cest_id: itemMeta.invalid_cest_id,
          had_fiscal_data_error: itemMeta.had_fiscal_data_error,
        });
      }
    });
  });

  return items;
};

// The API paginates requests, not items: `count` is the total of requests, so a
// page can show more rows than `items_per_page` when a request has several items.
export const listTaxReceiptRequestsWithMissingFiscalData = async (
  query: string
): Promise<{data: FiscalDataListItem[]; count: number}> => {
  const separator = query ? '&' : '';
  return axios
    .get(
      `${TAX_RECEIPT_API_URL}/tax-receipt-requests?status=missing_fiscal_data${separator}${query}`
    )
    .then(
      (
        resp: AxiosResponse<{result: TaxReceiptRequest[]; resultCount: number}>
      ) => ({
        data: flattenItemsWithMissingFiscalData(resp.data.result),
        count: resp.data.resultCount,
      })
    );
};

export const getCategoryWithTaxonomy = async (
  searchTerm: string
): Promise<CategoryWithTaxonomy[]> => {
  return axios
    .get(
      `${CATEGORIES_CATALOG_API_URL}/category-tree/item-with-taxonomy?with_taxonomy_info=true&name=${encodeURIComponent(
        searchTerm
      )}`
    )
    .then(
      (resp: AxiosResponse<{data: CategoryWithTaxonomy[]}>) => resp.data.data
    );
};

export const updateTaxReceiptRequestFiscalData = async (
  internalRefId: string,
  payload: FiscalDataUpdatePayload
): Promise<void> => {
  return axios
    .put(
      `${TAX_RECEIPT_API_URL}/tax-receipt-requests/${internalRefId}`,
      payload
    )
    .then((resp: AxiosResponse<void>) => resp.data);
};
